import { useEffect, useId, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { BookmarkIcon, ReceiptPercentIcon } from "@heroicons/react/24/solid";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useAppContext } from "@context/AppContext";
import { useSchemeSummary } from "@hooks/query/zenith/useSchemeSummary";
import Select from "react-select";
import Label from "@components/form/Label";
import Card from "@components/layout/Card";
import Loading from "@components/shared/Loading";
import SchemeSummaryCard from "@components/shared/scheme_dashboard/SchemeSummaryCard";
import ZoneUsageCard from "@components/shared/scheme_dashboard/ZoneUsageCard";
import { formatSchemePeriod } from "@utils/schemeDay";
import { percentOf } from "./percentOf";

// A carousel entry. The standing one states the scheme's accounting period; until scheme
// announcements have a source (290962), it is the only one.
type Announcement = {
  title: string;
  tag: string;
  body: string;
};

// GET /subscribers/schemes/summary; volumes are litres, as formatVolume expects.
type WaterClass = {
  id: string;
  name: string;
  volume: number;
  // Null between accounting periods.
  usage: number | null;
  // The announced allocation as a fraction; null when none is announced.
  rate: number | null;
};

type Zone = {
  id: string;
  name: string;
  identifier: string;
  subscriberCount: number;
  waterClasses: WaterClass[];
};

type Scheme = {
  id: string;
  name: string;
  volume: number;
  // Null between accounting periods.
  usage: number | null;
  subscriberCount: number;
  // Null between accounting periods.
  accountingPeriod: { periodStart: string; periodEnd: string } | null;
  zones: Zone[];
};

const SchemeDashboard: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { setInfoPanel } = useAppContext();
  const { data: scheme } = useSchemeSummary();

  useEffect(() => {
    setInfoPanel(<p>{t("zenith.scheme_dashboard.info")}</p>);
  }, [t, setInfoPanel]);

  if (!scheme) {
    return (
      <div className="pt-20">
        <Loading />
      </div>
    );
  }

  // What each period figure, and the standing announcement's period, says in
  // its place between accounting periods. Read from the summary's own period,
  // so the notice and the figures can never disagree.
  const periodNotice =
    scheme.accountingPeriod === null
      ? t("zenith.dashboard.no_active_period")
      : undefined;
  const announcements: Announcement[] = [
    {
      title: scheme.name,
      tag: t("zenith.scheme_dashboard.standing"),
      body:
        periodNotice ??
        `${t("common.current_accounting_period")}: ${formatSchemePeriod(
          scheme.accountingPeriod,
        )}`,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <AnnouncementsCarousel announcements={announcements} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Summary scheme={scheme} periodNotice={periodNotice} />
        {scheme.zones.length ? (
          // Keyed so a new scheme starts on its own first zone.
          <AnnouncedAllocations key={scheme.id} zones={scheme.zones} />
        ) : null}
      </div>
      <ZoneCards zones={scheme.zones} periodNotice={periodNotice} />
    </div>
  );
};

const ROTATE_MS = 9000;

const AnnouncementsCarousel: React.FunctionComponent<{
  announcements: Announcement[];
}> = ({ announcements }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  // Rotation holds while the customer points at or tabs into the carousel.
  const paused = hovered || focused;
  const count = announcements.length;
  const step = (by: number) => setIndex(i => (i + by + count) % count);

  useEffect(() => {
    if (paused) return;
    // Restarts on every move, so a manual step gets a full interval.
    const timer = setTimeout(() => setIndex((index + 1) % count), ROTATE_MS);
    return () => clearTimeout(timer);
  }, [index, paused, count]);

  const { title, tag, body } = announcements[index];

  return (
    <section
      aria-roledescription="carousel"
      aria-label={t("zenith.scheme_dashboard.announcements") as string}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
      }}
      className="flex items-center gap-4 rounded-lg border border-green-200 bg-green-50 px-5 py-4"
    >
      <CarouselButton
        icon={ChevronLeftIcon}
        label={t("zenith.scheme_dashboard.previous_announcement")}
        onClick={() => step(-1)}
      />
      <div
        aria-live={paused ? "polite" : "off"}
        className="flex min-h-18 min-w-0 flex-1 items-start gap-3.5"
      >
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-700">
          <BookmarkIcon className="h-4 w-4 text-white" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="mb-1 flex flex-wrap items-center gap-2.5">
            <h2 className="text-lg font-bold text-primary-2">{title}</h2>
            <span className="rounded-full border border-green-200 bg-white px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-green-700">
              {tag}
            </span>
          </div>
          <p className="text-sm">{body}</p>
        </div>
      </div>
      <div className="flex shrink-0 gap-1.5">
        {announcements.map((announcement, i) => (
          <button
            key={announcement.title}
            type="button"
            aria-label={
              t("zenith.scheme_dashboard.announcement_n", {
                n: i + 1,
              }) as string
            }
            aria-current={i === index || undefined}
            onClick={() => setIndex(i)}
            className={classNames(
              "h-2 w-2 rounded-full",
              i === index ? "bg-green-700" : "bg-green-200",
            )}
          />
        ))}
      </div>
      <CarouselButton
        icon={ChevronRightIcon}
        label={t("zenith.scheme_dashboard.next_announcement")}
        onClick={() => step(1)}
      />
    </section>
  );
};

const CarouselButton: React.FunctionComponent<{
  icon: React.ElementType;
  label: string;
  onClick: () => void;
}> = ({ icon: Icon, label, onClick }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-green-200 bg-white text-primary-2"
  >
    <Icon className="h-4 w-4" aria-hidden="true" />
  </button>
);

const Panel: React.FunctionComponent<{
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}> = ({ icon: Icon, title, children }) => {
  const titleId = useId();

  return (
    <section aria-labelledby={titleId}>
      <Card className="flex h-full flex-col px-5 py-4">
        <h2
          id={titleId}
          className="mb-2 flex items-center gap-2.5 font-bold text-primary-2"
        >
          <Icon className="h-5 w-5 text-primary-1" aria-hidden="true" />
          {title}
        </h2>
        {children}
      </Card>
    </section>
  );
};

const Summary: React.FunctionComponent<{
  scheme: Scheme;
  periodNotice?: string;
}> = ({ scheme, periodNotice }) => {
  const { t } = useTranslation();
  // Null only between accounting periods, when the notice stands in place of the split.
  const usage = scheme.usage ?? 0;
  const available = scheme.volume - usage;

  return (
    <SchemeSummaryCard
      landmark
      className="h-full"
      title={t("zenith.scheme_dashboard.summary")}
      labels={{
        volume: t("zenith.scheme_dashboard.total_volume"),
        subscribers: t("zenith.scheme_dashboard.total_subscribers"),
        used: t("zenith.scheme_dashboard.consumed_volume"),
        remaining: t("zenith.scheme_dashboard.available_volume"),
      }}
      volume={scheme.volume}
      subscriberCount={scheme.subscriberCount}
      used={{
        volume: usage,
        percent: percentOf(usage, scheme.volume),
      }}
      remaining={{
        volume: available,
        percent: percentOf(available, scheme.volume),
      }}
      periodNotice={periodNotice}
    />
  );
};

// Each zone is an allocation group, named by its identifier.
const AnnouncedAllocations: React.FunctionComponent<{
  zones: Zone[];
}> = ({ zones }) => {
  const { t } = useTranslation();
  const pickerId = useId();
  const [zoneId, setZoneId] = useState(zones[0].id);
  const zone = zones.find(z => z.id === zoneId) ?? zones[0];

  return (
    <Panel
      icon={ReceiptPercentIcon}
      title={t("zenith.scheme_dashboard.announced_allocations")}
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-5 pb-2">
        <div className="w-48">
          <Label htmlFor={pickerId}>
            {t("zenith.scheme_dashboard.allocation_group")}
          </Label>
          {/* A choice, not a filter: one group always shows, so it can't be cleared. */}
          <Select
            inputId={pickerId}
            classNamePrefix="allocation-group"
            options={zones.map(({ id, identifier }) => ({
              value: id,
              label: identifier,
            }))}
            value={{ value: zone.id, label: zone.identifier }}
            onChange={option => option && setZoneId(option.value)}
          />
        </div>
        <div className="flex w-full justify-around gap-2.5">
          {zone.waterClasses.map(({ id, name, rate }) => (
            <Gauge
              key={id}
              waterClass={name}
              pct={rate === null ? null : Math.round(rate * 100)}
            />
          ))}
        </div>
      </div>
    </Panel>
  );
};

// Tailwind fill classes, so the arc keeps the theme's colours.
const gaugeTone = (pct: number | null) => {
  if (pct === null) return { disc: "bg-white", arc: "fill-gray-800" };
  if (pct === 100) return { disc: "bg-sky-100", arc: "fill-primary-1" };
  return { disc: "bg-orange-100", arc: "fill-red-500" };
};

const Gauge: React.FunctionComponent<{
  waterClass: string;
  pct: number | null;
}> = ({ waterClass, pct }) => {
  const { t } = useTranslation();
  const { disc, arc } = gaugeTone(pct);
  // Nothing announced draws a full dark ring around a "-".
  const arcPct = pct ?? 100;

  return (
    <figure className="flex min-w-0 flex-1 flex-col items-center gap-2">
      <figcaption className="flex h-8 items-end text-center text-xs font-medium text-gray-500">
        {waterClass}
      </figcaption>
      <div
        className={`relative aspect-square w-full max-w-20 rounded-full ${disc}`}
      >
        <ResponsiveContainer>
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={[{ value: arcPct }, { value: 100 - arcPct }]}
              dataKey="value"
              innerRadius="82%"
              outerRadius="100%"
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive={false}
            >
              <Cell className={arc} />
              <Cell className="fill-transparent" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold">
          {pct === null ? (
            <>
              <span aria-hidden="true">-</span>
              <span className="sr-only">
                {t("zenith.scheme_dashboard.not_announced")}
              </span>
            </>
          ) : (
            `${pct}%`
          )}
        </div>
      </div>
    </figure>
  );
};

const ZoneCards: React.FunctionComponent<{
  zones: Zone[];
  periodNotice?: string;
}> = ({ zones, periodNotice }) => {
  const { t } = useTranslation();

  return (
    <ul
      aria-label={t("zenith.scheme_dashboard.zones") as string}
      // Columns follow the content width, which the info panel narrows.
      className="grid grid-cols-fill-60 gap-4"
    >
      {zones.map(zone => (
        <ZoneCard key={zone.id} zone={zone} periodNotice={periodNotice} />
      ))}
    </ul>
  );
};

const ZoneCard: React.FunctionComponent<{
  zone: Zone;
  periodNotice?: string;
}> = ({ zone, periodNotice }) => {
  const { t } = useTranslation();

  return (
    <li>
      <ZoneUsageCard
        landmark
        className="h-full"
        name={zone.name}
        subscriberCount={zone.subscriberCount}
        subscribersLabel={t("zenith.scheme_dashboard.subscribers")}
        waterClasses={zone.waterClasses.map(({ id, name, usage, volume }) => {
          // Null only between accounting periods, when the notice stands in its place.
          const used = usage ?? 0;

          return {
            id,
            name,
            usage: used,
            // The share of the class's volume used; ledger corrections can leave usage negative.
            share: volume ? Math.max(0, Math.min(1, used / volume)) : 0,
          };
        })}
        periodNotice={periodNotice}
      />
    </li>
  );
};

export default SchemeDashboard;
